<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateMeterReadings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    items: {
      type: Array,
      required: true,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const notes = ref(null);
  const irentWriteback = ref(false);
  const skipMetersNotFound = ref(false);
  const irentWritebackSkipMetersNotFound = ref(false);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    notes: notes.value,
    irentWriteback: irentWriteback.value,
    skipMetersNotFound: skipMetersNotFound.value,
    irentWritebackSkipMetersNotFound: irentWritebackSkipMetersNotFound.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'notes' },
      { name: 'irentWriteback' },
      { name: 'skipMetersNotFound' },
      { name: 'irentWritebackSkipMetersNotFound' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      notes,
      irentWriteback,
      skipMetersNotFound,
      irentWritebackSkipMetersNotFound,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('CreateMeterReadings', 'Create Meter Readings'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_r0x3fggbc_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_register_burnqjw1i_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_1040167445267876_notification_yzc22yd6r_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.ngettext(
        'One meter reading created.',
        '${n} meter readings created.',
        $event.data.resultLength
      ),
    };
  };
  const act_1040167445267876_notification_yzc22yd6r_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_1040167445267876_closeModal_jq0abgbbx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_reloadGrid_x6dabch7p_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_1040167445267876_request_e9crktwnj_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_meter_reading',
      data: props.items
        .filter((p) => !!p.meter_id)
        .map((p) => ({
          meter_id: p.meter_id,
          value: p.value,
          unit: p.unit,
          notes: notes.value,
          reading_type: 'automated',
          reading_date: $fn.value.formatDate(
            $fn.value.parseISODate(p.timestamp),
            'yyyy-MM-dd'
          ),
          reading_time: $fn.value.formatDate(
            $fn.value.parseISODate(p.timestamp),
            'hh:mm'
          ),
        })),
      parameters: {
        'irent-writeback': irentWriteback.value,
        'skip-meters-not-found': skipMetersNotFound.value,
        'irent-writeback-skip-meters-not-found':
          irentWritebackSkipMetersNotFound.value,
      },
    };
  };

  const act_7315092382398562_closeModal_r0x3fggbc =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_r0x3fggbc_getActionArgs,
    });
  const inst_evh_7315092382398562_close_g0e9gvu5y =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_r0x3fggbc,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_g0e9gvu5y],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_2voklq32v =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_wsyuiurzd =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2voklq32v,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_wsyuiurzd],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_tbf691x52 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_al0rhprhu =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_tbf691x52,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_al0rhprhu],
    event: 'close',
  });
  const act_1040167445267876_register_burnqjw1i =
    new actionTypes.RegisterAction({
      actionArgs: act_1040167445267876_register_burnqjw1i_getActionArgs,
    });
  const inst_evh_1040167445267876_success_neervk2cw =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_register_burnqjw1i,
      event: 'success',
      displayName: 'register',
    });
  const act_1040167445267876_notification_yzc22yd6r =
    new actionTypes.NotificationAction({
      actionArgs: act_1040167445267876_notification_yzc22yd6r_getActionArgs,
      when: act_1040167445267876_notification_yzc22yd6r_getWhen,
    });
  const inst_evh_1040167445267876_success_svk1ed4nl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_notification_yzc22yd6r,
      event: 'success',
      displayName: 'notification',
    });
  const act_1040167445267876_closeModal_jq0abgbbx =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_jq0abgbbx_getActionArgs,
    });
  const inst_evh_1040167445267876_success_2g8n7mqk0 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_jq0abgbbx,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_reloadGrid_x6dabch7p =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_x6dabch7p_getActionArgs,
    });
  const inst_evh_1040167445267876_success_8fnyt1xsj =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_x6dabch7p,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_request_e9crktwnj = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_e9crktwnj_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_neervk2cw,
      inst_evh_1040167445267876_success_svk1ed4nl,
      inst_evh_1040167445267876_success_2g8n7mqk0,
      inst_evh_1040167445267876_success_8fnyt1xsj,
    ],
  });
  const inst_evh_1040167445267876_submit_9e3m6iyah =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_e9crktwnj,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_9e3m6iyah],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_ez1x70x39 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_qe1edsc2q =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_ez1x70x39,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_qe1edsc2q],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_g0e9gvu5y,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_wsyuiurzd,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_al0rhprhu,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_neervk2cw,
    inst_evh_1040167445267876_success_svk1ed4nl,
    inst_evh_1040167445267876_success_2g8n7mqk0,
    inst_evh_1040167445267876_success_8fnyt1xsj,
    inst_evh_1040167445267876_submit_9e3m6iyah,
    submit: inst_evh_1040167445267876_submit_9e3m6iyah,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_qe1edsc2q,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_g0e9gvu5y = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_g0e9gvu5y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_wsyuiurzd = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_wsyuiurzd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_al0rhprhu = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_al0rhprhu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_neervk2cw = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_neervk2cw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_svk1ed4nl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_svk1ed4nl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_2g8n7mqk0 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_2g8n7mqk0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_8fnyt1xsj = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_8fnyt1xsj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_9e3m6iyah = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_9e3m6iyah.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_qe1edsc2q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_qe1edsc2q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateMeterReadings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="createOptions"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="notes"
          :label="pgettext('CreateMeterReadings', 'Notes')"
        >
          <ItpFormTextarea name="notes" v-model="notes"> </ItpFormTextarea>
        </ItpFormGroup>
        <ItpFormGroup
          name="irentWriteback"
          :description="
            pgettext(
              'CreateMeterReadings',
              'Save values to the legacy database, too.'
            )
          "
          row
          :label-cols-md="0"
          :content-cols-md="12"
        >
          <ItpFormCheckbox
            name="irentWriteback"
            v-model="irentWriteback"
            :label="pgettext('CreateMeterReadings', 'IntegroRent writeback')"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="irentWriteback"
          :description="
            pgettext(
              'GenerateMeterReading',
              'Skip writeback to the legacy database if the serial number is not found in the legacy database.'
            )
          "
          row
          :label-cols-md="0"
          :content-cols-md="12"
        >
          <ItpFormCheckbox
            name="irentWritebackSkipMetersNotFound"
            v-model="irentWritebackSkipMetersNotFound"
            :label="
              pgettext(
                'CreateMeterReadings',
                'IntegroRent writeback - skip not found'
              )
            "
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="skipMetersNotFound"
          :description="
            pgettext(
              'CreateMeterReadings',
              'Skip meters not found in the database.'
            )
          "
          row
          :label-cols-md="0"
          :content-cols-md="12"
        >
          <ItpFormCheckbox
            name="skipMetersNotFound"
            v-model="skipMetersNotFound"
            :label="pgettext('CreateMeterReadings', 'Skip not found')"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.createOptions && forms.createOptions.id"
          :text="pgettext('MeterMeasurementGenerateButton', 'Create Readings')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('CreateMeterReadings', 'Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
